import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Button, DatePicker, Modal, TextInput } from '../../../shared/ui'
import { isValidChip, normalizeChip } from '../../../staff/lib/chip'
import {
  heuteIso, kartenFehler, plusJahrIso, useCreateCard, usePatchCard, type SchoolCard,
} from './kartenApi'

interface Props {
  open: boolean
  schoolId: string
  /** Bearbeiten, wenn gesetzt; sonst neue Karte. */
  initial: SchoolCard | null
  onClose: () => void
}

// Gleiche Regel wie staff/login/ChipStep.tsx: ein Leser tippt den Burst in <300 ms,
// übernommen werden die ersten 8 Hex-Zeichen (manche Leser hängen eine Prüfsumme an).
const SCAN_WINDOW_MS = 300
const SETTLE_MS = 130

export default function CardEditDialog({ open, schoolId, initial, onClose }: Props) {
  const isEdit = !!initial
  const create = useCreateCard()
  const patch = usePatchCard()

  const [chip, setChip] = useState('')
  const [bezeichnung, setBezeichnung] = useState('')
  const [gueltigVon, setGueltigVon] = useState(heuteIso())
  const [gueltigBis, setGueltigBis] = useState(plusJahrIso(heuteIso()))
  const [error, setError] = useState<string | null>(null)

  const scanRef = useRef<HTMLInputElement>(null)
  const bufferRef = useRef('')
  const firstKeyAtRef = useRef<number | null>(null)
  const lastKeyAtRef = useRef<number | null>(null)
  const idleTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open) return
    const heute = heuteIso()
    setChip(initial?.chip ?? '')
    setBezeichnung(initial?.bezeichnung ?? '')
    setGueltigVon(initial?.gueltigVon ?? heute)
    setGueltigBis(initial ? (initial.gueltigBis ?? '') : plusJahrIso(heute))
    setError(null)
    if (initial) return
    const t = window.setTimeout(() => scanRef.current?.focus(), 80)
    return () => window.clearTimeout(t)
  }, [open, initial])

  useEffect(() => () => { if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current) }, [])

  function resetBuffer() {
    bufferRef.current = ''
    firstKeyAtRef.current = null
    lastKeyAtRef.current = null
    if (idleTimerRef.current != null) { window.clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    if (scanRef.current) scanRef.current.value = ''
  }

  function finalizeBuffer() {
    const raw = bufferRef.current
    const startedAt = firstKeyAtRef.current
    const endedAt = lastKeyAtRef.current
    resetBuffer()
    if (!raw || startedAt == null || endedAt == null) return
    const candidate = normalizeChip(raw).slice(0, 8)
    if (endedAt - startedAt < SCAN_WINDOW_MS && isValidChip(candidate)) {
      setChip(candidate)
      setError(null)
    }
  }

  function onScanKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); finalizeBuffer(); return }
    if (!/^[0-9a-fA-F]$/.test(e.key)) return
    const now = Date.now()
    if (bufferRef.current.length === 0) firstKeyAtRef.current = now
    lastKeyAtRef.current = now
    bufferRef.current += e.key
    if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current)
    idleTimerRef.current = window.setTimeout(finalizeBuffer, SETTLE_MS)
  }

  const chipOk = isValidChip(chip)
  const datesOk = !!gueltigVon && (!gueltigBis || gueltigBis >= gueltigVon)
  const canSave = (isEdit || chipOk) && bezeichnung.trim().length > 0 && datesOk
  const busy = create.isPending || patch.isPending

  async function save() {
    if (!canSave) return
    try {
      setError(null)
      if (initial) {
        await patch.mutateAsync({
          id: initial.id,
          bezeichnung: bezeichnung.trim(),
          gueltigVon,
          gueltigBis: gueltigBis || null,
        })
      } else {
        await create.mutateAsync({
          schoolId,
          chip,
          bezeichnung: bezeichnung.trim(),
          gueltigVon,
          gueltigBis: gueltigBis || null,
        })
      }
      onClose()
    } catch (e) {
      setError(kartenFehler(e))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? 'Karte bearbeiten' : 'Karte hinzufügen'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button variant="primary" onClick={save} disabled={!canSave || busy} loading={busy}>
            {isEdit ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!isEdit && (
          <section className="space-y-2">
            {/* Unsichtbares Fangfeld für den Kartenleser (Tastatur-Emulation) */}
            <div className="sr-only">
              <TextInput ref={scanRef} aria-label="Kartenleser" autoComplete="off" onKeyDown={onScanKeyDown} />
            </div>
            <TextInput
              label="Kartennummer"
              required
              mono
              value={chip}
              onChange={(e) => setChip(normalizeChip(e.target.value).slice(0, 8))}
              placeholder="8 Zeichen, z. B. 04A1B2C3"
              maxLength={8}
              autoComplete="off"
              error={chip.length > 0 && !chipOk ? 'Die Nummer hat genau 8 Zeichen (0–9, A–F).' : undefined}
            />
            <p className="text-[11px] text-on-surface-variant">
              {chipOk
                ? 'Nummer übernommen.'
                : 'Karte jetzt auf den Leser legen — oder die Nummer eintippen.'}
            </p>
          </section>
        )}

        {isEdit && (
          <p className="text-sm text-on-surface-variant">
            Kartennummer <span className="font-mono tabular-nums text-on-surface">{initial!.chip}</span>
          </p>
        )}

        <TextInput
          label="Bezeichnung"
          required
          value={bezeichnung}
          onChange={(e) => setBezeichnung(e.target.value)}
          placeholder="z. B. Frau Muster"
          maxLength={100}
        />
        <p className="text-[11px] text-on-surface-variant -mt-3">Am besten der Name der Person.</p>

        <div className="grid grid-cols-2 gap-4">
          <DatePicker label="Gültig ab" value={gueltigVon} onChange={setGueltigVon} usePortal menuZIndex={100001} />
          <DatePicker label="Gültig bis" value={gueltigBis} onChange={setGueltigBis} min={gueltigVon} usePortal menuZIndex={100001} />
        </div>
        {gueltigBis && gueltigBis < gueltigVon && (
          <p className="text-xs text-rose-600">„Gültig bis“ liegt vor „Gültig ab“.</p>
        )}

        {error && <p className="text-sm text-rose-600">{error}</p>}
      </div>
    </Modal>
  )
}
